import { z } from 'zod'
import {
  type AssistantReasoningEffort,
  assistantReasoningEffortSchema,
} from './assistant-reasoning.ts'
import { ROLES, type Role } from './permissions.ts'

const id = z.string().trim().min(1).max(200)
const model = z.string().trim().min(1).max(200)
const version = z.number().int().positive()
const models = z
  .array(model)
  .max(500)
  .refine((items) => new Set(items).size === items.length)
export const assistantAttachmentSchema = z
  .object({
    storedObjectId: id.optional(),
    documentId: id.optional(),
    name: z.string().max(255).optional(),
  })
  .strict()
  .refine((input) => Boolean(input.storedObjectId || input.documentId))
export type AssistantAttachment = z.infer<typeof assistantAttachmentSchema>
const partBase = { id, at: z.iso.datetime({ offset: true }) }
export const assistantPartSchema = z.discriminatedUnion('type', [
  z.object({
    ...partBase,
    type: z.literal('text'),
    role: z.enum(['user', 'assistant']),
    text: z.string(),
    modelId: z.string().optional(),
    files: z.array(z.string()).optional(),
    streaming: z.boolean().optional(),
  }),
  z.object({
    ...partBase,
    type: z.literal('tool_call'),
    label: z.string(),
    status: z.enum(['pending', 'complete']),
    toolName: z.string().optional(),
    arguments: z.unknown().optional(),
    callId: z.string().optional(),
  }),
  z.object({
    ...partBase,
    type: z.literal('tool_result'),
    text: z.string(),
    callId: z.string().optional(),
    output: z.unknown().optional(),
  }),
  z.object({
    ...partBase,
    type: z.literal('proposal'),
    proposalId: z.string().optional(),
    title: z.string(),
    description: z.string(),
    status: z.enum(['pending', 'confirmed', 'discarded']).optional(),
    version: z.number().int().positive().optional(),
  }),
  z.object({ ...partBase, type: z.literal('error'), text: z.string(), retryText: z.string() }),
])
export type AssistantPart = z.infer<typeof assistantPartSchema>
export interface AssistantConversation {
  id: string
  ownerId: string
  title: string
  modelId: string | null
  reasoningEffort: AssistantReasoningEffort | null
  version: number
  createdAt: string
  updatedAt: string
  messages: AssistantPart[]
}
export interface AssistantEffectiveAccess {
  configured: boolean
  fake: boolean
  enabled: boolean
  models: string[]
  defaultModel: string | null
}
export interface AssistantRoleAccess {
  role: Role
  enabled: boolean
  models: string[]
  defaultModel: string | null
  version: number
}
export interface AssistantUsage {
  month: string
  inputTokens: number
  outputTokens: number
  totalTokens: number
}
export interface AssistantUserAccess {
  userId: string
  enabled: boolean | null
  allowModels: string[]
  denyModels: string[]
  defaultModel: string | null
  version: number
  effective: AssistantEffectiveAccess
  usage: AssistantUsage
}
export interface AssistantSettings {
  configured: boolean
  fake: boolean
  catalogue: string[]
  enabledModels: string[]
  version: number
  refreshedAt: string | null
  roles: AssistantRoleAccess[]
}
export const assistantSettingsSchema = z.object({ version, enabledModels: models }).strict()
export const assistantRoleAccessSchema = z
  .object({
    role: z.enum(ROLES),
    version,
    enabled: z.boolean(),
    models,
    defaultModel: model.nullable(),
  })
  .strict()
export const assistantUserAccessSchema = z
  .object({
    userId: id,
    version,
    enabled: z.boolean().nullable(),
    allowModels: models,
    denyModels: models,
    defaultModel: model.nullable(),
  })
  .strict()
export const assistantConversationCreateSchema = z
  .object({
    modelId: model.nullable().optional(),
    reasoningEffort: assistantReasoningEffortSchema.nullable().optional(),
    title: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
export const assistantConversationRenameSchema = z
  .object({ id, version, title: z.string().trim().min(1).max(200) })
  .strict()
export const assistantConversationDeleteSchema = z.object({ id, version }).strict()
export const assistantMessageCreateSchema = z
  .object({
    requestId: z.uuid(),
    text: z.string().trim().max(40_000),
    modelId: model.nullable().optional(),
    reasoningEffort: assistantReasoningEffortSchema.nullable().optional(),
    attachments: z.array(assistantAttachmentSchema).max(10).default([]),
  })
  .strict()
  .refine((input) => input.text.length > 0 || input.attachments.length > 0)
